/** ユーザー一覧の右パネル。概要の要点と「全画面で開く」。 */
import { Alert, Button } from "antd";
import { useState } from "react";

import { jst } from "../../lib/format";
import { Initials, KeyValueList, PanelSection, SidePanel, StatusTag, TimeAgo } from "../../ui";
import { errorText, useIdpPerms, useIdpUser } from "./hooks";
import { UserEditDrawer } from "./UserEditDrawer";
import { userStatusTone } from "./userOptions";

export function UserPanel({ id, onClose }: { id: string; onClose: () => void }) {
  const { canAdmin } = useIdpPerms();
  const [editing, setEditing] = useState(false);
  const query = useIdpUser(id);
  const u = query.data;

  if (query.isError) {
    return (
      <SidePanel title="ユーザー" onClose={onClose}>
        <Alert type="error" showIcon message="ユーザーを取得できなかった" description={errorText(query.error)} />
      </SidePanel>
    );
  }

  return (
    <SidePanel
      title={u?.displayName || u?.username || "ユーザー"}
      loading={query.isLoading || !u}
      avatar={u && <Initials name={u.displayName || u.username} />}
      status={
        u && (
          <StatusTag tone={userStatusTone(u.status)} dot>
            {u.status}
          </StatusTag>
        )
      }
      sub={
        u && (
          <>
            <div className="ui-num">{u.username}</div>
            {u.email && <div>{u.email}</div>}
          </>
        )
      }
      fullHref={`/idp/users/${encodeURIComponent(id)}`}
      onClose={onClose}
      actions={
        canAdmin ? (
          <Button size="small" onClick={() => setEditing(true)}>
            編集
          </Button>
        ) : undefined
      }
    >
      {u && (
        <>
          <PanelSection title="概要">
            <KeyValueList
              narrow
              items={[
                {
                  label: "最新ログイン",
                  always: true,
                  value: u.accessInfo.lastLogin ? <TimeAgo iso={u.accessInfo.lastLogin} /> : "",
                },
                {
                  label: "MFA",
                  always: true,
                  value: u.otp.registered ? "あり" : "なし",
                },
                {
                  label: "ポリシー",
                  always: true,
                  value: u.policy?.effectiveGroupName
                    ? `${u.policy.effectiveGroupName}${u.policy.effectiveIsDefault ? "(既定)" : ""}`
                    : "なし(素通り)",
                },
                { label: "セッション", always: true, value: `${u.accessInfo.sessions} 件` },
                {
                  label: "連続失敗",
                  value: u.accessInfo.failureCount
                    ? `${u.accessInfo.failureCount} 回${u.accessInfo.locked ? "(一時ロック中)" : ""}`
                    : "",
                },
                { label: "作成日時", value: jst(u.profile.createdAt) },
              ]}
              unsetLabel="なし"
            />
          </PanelSection>
          <PanelSection title="権限">
            {u.access.effectiveRoles.length ? (
              <div>{u.access.effectiveRoles.join("、")}</div>
            ) : (
              <div style={{ color: "var(--ux-text-disabled)" }}>なし</div>
            )}
          </PanelSection>
          <UserEditDrawer user={u} open={editing} onClose={() => setEditing(false)} />
        </>
      )}
    </SidePanel>
  );
}
